import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const InvestmentCalculator: React.FC = () => {
  const [initialInvestment, setInitialInvestment] = useState<number>(10000);
  const [contributionAmount, setContributionAmount] = useState<number>(500);
  const [contributionFrequency, setContributionFrequency] = useState<string>("monthly");
  const [interestRate, setInterestRate] = useState<number>(7);
  const [investmentLength, setInvestmentLength] = useState<number>(10);
  const [compoundingFrequency, setCompoundingFrequency] = useState<string>("annually");
  
  const [futureValue, setFutureValue] = useState<number>(0);
  const [totalContributions, setTotalContributions] = useState<number>(0);
  const [totalInterest, setTotalInterest] = useState<number>(0);

  // Add ref to the calculator container
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Add effect to prevent scrolling on focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const calculateInvestment = () => {
    try {
      // Validation
      if (initialInvestment < 0 || contributionAmount < 0 || interestRate < 0 || investmentLength <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }

      // Convert annual interest rate to decimal
      const annualRate = interestRate / 100;
      
      // Determine number of compounds per year
      let compoundsPerYear = 1; // Default: annually
      if (compoundingFrequency === "semi-annually") compoundsPerYear = 2;
      else if (compoundingFrequency === "quarterly") compoundsPerYear = 4;
      else if (compoundingFrequency === "monthly") compoundsPerYear = 12;
      else if (compoundingFrequency === "daily") compoundsPerYear = 365;
      
      // Determine contributions per year
      let contributionsPerYear = 12; // Default: monthly
      if (contributionFrequency === "annually") contributionsPerYear = 1;
      else if (contributionFrequency === "semi-annually") contributionsPerYear = 2;
      else if (contributionFrequency === "quarterly") contributionsPerYear = 4;
      else if (contributionFrequency === "bi-weekly") contributionsPerYear = 26;
      else if (contributionFrequency === "weekly") contributionsPerYear = 52;
      
      // Calculate total periods
      const totalCompoundingPeriods = compoundsPerYear * investmentLength;
      const ratePerPeriod = annualRate / compoundsPerYear;
      
      // Calculate future value of initial principal
      const principalFutureValue = initialInvestment * Math.pow(1 + ratePerPeriod, totalCompoundingPeriods);
      
      // Calculate future value of periodic contributions
      const contributionPerPeriod = contributionAmount * (contributionsPerYear / compoundsPerYear);
      const contributionFV = contributionPerPeriod * 
        (Math.pow(1 + ratePerPeriod, totalCompoundingPeriods) - 1) / ratePerPeriod;
      
      // Calculate total future value
      const totalFutureValue = principalFutureValue + contributionFV;
      
      // Calculate total contributions
      const totalContributionsValue = initialInvestment + (contributionAmount * contributionsPerYear * investmentLength);
      
      // Calculate total interest earned
      const totalInterestValue = totalFutureValue - totalContributionsValue;
      
      setFutureValue(totalFutureValue);
      setTotalContributions(totalContributionsValue);
      setTotalInterest(totalInterestValue);

      toast({
        title: "Calculation complete",
        description: "Your investment growth has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6" ref={calculatorRef}>
      <Tabs defaultValue="calculator">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="info">Information</TabsTrigger>
        </TabsList>
        
        <TabsContent value="calculator" className="space-y-6 pt-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initialInvestment">Initial Investment</Label>
                <Input
                  id="initialInvestment"
                  type="number"
                  value={initialInvestment}
                  onChange={(e) => setInitialInvestment(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contributionAmount">Contribution Amount</Label>
                <Input
                  id="contributionAmount"
                  type="number"
                  value={contributionAmount}
                  onChange={(e) => setContributionAmount(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contributionFrequency">Contribution Frequency</Label>
                <Select 
                  value={contributionFrequency} 
                  onValueChange={setContributionFrequency}
                >
                  <SelectTrigger id="contributionFrequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi-annually">Semi-Annually</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="interestRate">Annual Interest Rate (%)</Label>
                <Input
                  id="interestRate"
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  step="0.1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="investmentLength">Investment Period (years)</Label>
                <Input
                  id="investmentLength"
                  type="number"
                  value={investmentLength}
                  onChange={(e) => setInvestmentLength(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="compoundingFrequency">Compounding Frequency</Label>
                <Select 
                  value={compoundingFrequency} 
                  onValueChange={setCompoundingFrequency}
                >
                  <SelectTrigger id="compoundingFrequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi-annually">Semi-Annually</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={calculateInvestment} className="mt-4 w-full">Calculate Investment Growth</Button>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Investment Summary</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Future Value</div>
                      <div className="text-xl font-bold text-primary">{futureValue ? formatCurrency(futureValue) : '-'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Contributions</div>
                      <div className="text-lg font-medium">{totalContributions ? formatCurrency(totalContributions) : '-'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Interest Earned</div>
                      <div className="text-lg font-medium">{totalInterest ? formatCurrency(totalInterest) : '-'}</div>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Return on Investment</div>
                      <div className="text-lg font-medium">
                        {totalContributions && totalInterest
                          ? `${((totalInterest / totalContributions) * 100).toFixed(2)}%`
                          : '-'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="info" className="space-y-4 pt-4">
          <div className="prose max-w-none">
            <h3>About Investment Calculations</h3>
            <p>
              This calculator uses the compound interest formula to project how your investments may grow over time.
              It considers both your initial investment and regular contributions.
            </p>
            
            <h4>Key Terms:</h4>
            <ul>
              <li><strong>Initial Investment:</strong> The amount you start with</li>
              <li><strong>Contribution Amount:</strong> How much you add regularly</li>
              <li><strong>Annual Interest Rate:</strong> Your expected rate of return</li>
              <li><strong>Compounding Frequency:</strong> How often interest is calculated</li>
            </ul>
            
            <div className="bg-muted p-4 rounded-md mt-4">
              <h4>Formula Used:</h4>
              <p><strong>Future Value = P(1+r)^n + PMT × ((1+r)^n-1)/r</strong></p>
              <p className="text-sm">
                Where:<br />
                P = Principal (initial investment)<br />
                PMT = Regular payment amount<br />
                r = Interest rate per period<br />
                n = Number of periods
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvestmentCalculator;
